import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Radio,
  RadioGroup,
  Checkbox,
  CheckboxGroup,
  Progress,
  Badge,
  useToast,
  Image,
  Heading,
  useColorModeValue,
  Input,
  FormControl,
  FormLabel,
  Link,
} from '@chakra-ui/react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { CheckIcon, StarIcon } from '@chakra-ui/icons';
import {
  Poll,
  PollOption,
  PollResultsResponse,
  votePoll,
  getPollResults,
  generateSessionToken,
} from '../../services/polls';
import { useUmami } from '../../hooks/useUmami';
import { useAuth } from '../../contexts/AuthContext';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PollCardProps {
  poll: Poll;
  hasVoted: boolean;
  isActive: boolean;
  canShowResults: boolean;
  onVoteSuccess?: () => void;
  // When true, render transparent card without own bg/border/shadow
  flat?: boolean;
}

const PollCard: React.FC<PollCardProps> = ({
  poll,
  hasVoted: initialHasVoted,
  isActive,
  canShowResults: initialCanShowResults,
  onVoteSuccess,
  flat = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { trackEvent } = useUmami();
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [hasVoted, setHasVoted] = useState(initialHasVoted);
  const [canShowResults, setCanShowResults] = useState(initialCanShowResults);
  const [results, setResults] = useState<any[]>([]);
  const [showingResults, setShowingResults] = useState(initialCanShowResults);
  const [voterName, setVoterName] = useState('');
  const [voterEmail, setVoterEmail] = useState('');
  const isRating = poll.type === 'rating';
  const ratingOptionsSorted = [...poll.options].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const maxRating = ratingOptionsSorted.length > 0
    ? Math.max(...ratingOptionsSorted.map(o => (o.display_order || 0))) || ratingOptionsSorted.length
    : 5;
  const [ratingValue, setRatingValue] = useState<number | null>(null);
  const selectedStyle = (poll as any).style || 'auto';
  const resolvedStyle = (() => {
    if (selectedStyle !== 'auto') return selectedStyle;
    if (isRating) return maxRating <= 5 ? 'rating-stars' : 'rating-scale';
    return 'choices-list';
  })();

  const selectOptionForRating = (value: number) => {
    setRatingValue(value);
    const byOrder = ratingOptionsSorted.find(o => (o.display_order || 0) === value);
    const fallback = ratingOptionsSorted[value - 1];
    const opt = byOrder || fallback;
    if (opt) {
      setSelectedOptions([opt.id]);
    }
  };

  const bgCard = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Live results polling when results are visible and allowed
  const showLiveResults = canShowResults && showingResults;
  const { data: liveResultsData } = useQuery<PollResultsResponse>({
    queryKey: ['poll-results', poll.id],
    queryFn: () => getPollResults(poll.id),
    enabled: showLiveResults,
    refetchInterval: 30000,
    refetchOnWindowFocus: false,
  });

  // Vote mutation
  const voteMutation = useMutation({
    mutationFn: () => {
      const sessionToken = generateSessionToken();
      return votePoll(poll.id, {
        option_ids: selectedOptions,
        session_token: sessionToken,
        voter_name: voterName || (isAuthenticated ? (user as any)?.name : undefined),
        voter_email: voterEmail || (isAuthenticated ? user?.email : undefined),
      });
    },
    onSuccess: async () => {
      setHasVoted(true);
      setCanShowResults(true);
      setShowingResults(true);
      
      // Fetch results
      try {
        const resultsData = await getPollResults(poll.id);
        setResults(resultsData.results);
      } catch (error) {
        console.error('Failed to fetch results:', error);
      }

      queryClient.invalidateQueries({ queryKey: ['polls'] });
      queryClient.invalidateQueries({ queryKey: ['poll', poll.id] });
      queryClient.invalidateQueries({ queryKey: ['poll-results', poll.id] });

      toast({
        title: 'Hlas zaznamenán!',
        description: 'Děkujeme za vaši účast v anketě.',
        status: 'success',
        duration: 3000,
      });

      // Analytics tracking (Umami + backend)
      try {
        trackEvent('Poll Vote', {
          poll_id: poll.id,
          poll_title: poll.title,
          type: poll.type,
          option_ids: selectedOptions,
          rating: ratingValue || undefined,
        });
      } catch (e) {
        // swallow
      }

      if (onVoteSuccess) {
        onVoteSuccess();
      }

      try {
        window.dispatchEvent(new CustomEvent('engagement:refresh'));
      } catch {}
    },
    onError: (error: any) => {
      toast({
        title: 'Chyba',
        description: error.response?.data?.error || 'Nepodařilo se zaznamenat váš hlas',
        status: 'error',
        duration: 5000,
      });
    },
  });

  const handleVote = () => {
    if (selectedOptions.length === 0) {
      toast({
        title: 'Vyberte možnost',
        description: 'Před hlasováním vyberte alespoň jednu možnost.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (poll.allow_multiple && selectedOptions.length > poll.max_choices) {
      toast({
        title: 'Příliš mnoho voleb',
        description: `Můžete vybrat maximálně ${poll.max_choices} možností.`,
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    voteMutation.mutate();
  };

  const handleSingleChoice = (value: string) => {
    setSelectedOptions([parseInt(value)]);
  };

  const handleMultipleChoice = (values: (string | number)[]) => {
    const numValues = values.map((v) => (typeof v === 'string' ? parseInt(v) : v));
    if (numValues.length <= poll.max_choices) {
      setSelectedOptions(numValues);
    }
  };

  const handleOptionClick = (optionId: number) => {
    if (poll.allow_multiple) {
      const isSelected = selectedOptions.includes(optionId);
      if (isSelected) {
        setSelectedOptions(selectedOptions.filter((id) => id !== optionId));
      } else {
        if (selectedOptions.length >= poll.max_choices) {
          toast({
            title: 'Příliš mnoho voleb',
            description: `Můžete vybrat maximálně ${poll.max_choices} možností.`,
            status: 'warning',
            duration: 3000,
          });
          return;
        }
        setSelectedOptions([...selectedOptions, optionId]);
      }
    } else {
      setSelectedOptions([optionId]);
    }
  };

  const loadResults = async () => {
    try {
      const resultsData = await getPollResults(poll.id);
      setResults(resultsData.results);
      setShowingResults(true);
    } catch (error: any) {
      toast({
        title: 'Chyba',
        description: 'Nepodařilo se načíst výsledky',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const calculatePercentage = (voteCount: number) => {
    if (poll.total_votes === 0) return 0;
    return (voteCount / poll.total_votes) * 100;
  };

  // Show results if available
  if (showingResults && canShowResults) {
    const totalVotesToShow = liveResultsData?.total_votes ?? poll.total_votes;
    const displayResults = liveResultsData?.results || (results.length > 0 ? results : poll.options.map(opt => ({
      option_id: opt.id,
      text: opt.text,
      vote_count: opt.vote_count,
      percentage: totalVotesToShow ? (opt.vote_count / totalVotesToShow) * 100 : 0,
      image_url: opt.image_url,
    })));

    return (
      <Box
        className={flat ? undefined : 'card'}
        bg={flat ? 'transparent' : bgCard}
        borderWidth={flat ? '0' : '1px'}
        borderColor={flat ? 'transparent' : borderColor}
        p={flat ? 0 : 6}
      >
        <VStack spacing={4} align="stretch">
          {poll.image_url && (
            <Image
              src={poll.image_url}
              alt={poll.title}
              borderRadius="lg"
              maxH="200px"
              objectFit="cover"
            />
          )}

          <HStack justify="space-between" align="start">
            <Heading size="md">{poll.title}</Heading>
            {hasVoted && (
              <Badge colorScheme="green" fontSize="sm">
                <HStack spacing={1}>
                  <CheckIcon boxSize={3} />
                  <Text>Hlasováno</Text>
                </HStack>
              </Badge>
            )}
          </HStack>

          {poll.description && (
            <Text fontSize="sm" color="gray.600">
              {poll.description}
            </Text>
          )}

          <VStack spacing={3} align="stretch">
            <Text fontWeight="bold" fontSize="sm" color="gray.500">
              Výsledky ({totalVotesToShow} hlasů)
            </Text>
            {displayResults.map((result) => (
              <Box key={result.option_id}>
                <HStack justify="space-between" mb={1}>
                  <Text fontWeight="medium">{result.text}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {result.vote_count} ({result.percentage.toFixed(1)}%)
                  </Text>
                </HStack>
                <Progress
                  value={result.percentage}
                  colorScheme="blue"
                  borderRadius="full"
                  size="sm"
                />
              </Box>
            ))}
          </VStack>
        </VStack>
      </Box>
    );
  }

  // Show voting form
  return (
    <Box
      className={flat ? undefined : 'card'}
      bg={flat ? 'transparent' : bgCard}
      borderWidth={flat ? '0' : '1px'}
      borderColor={flat ? 'transparent' : borderColor}
      p={flat ? 0 : 6}
    >
      <VStack spacing={4} align="stretch">
        {poll.image_url && (
          <Image
            src={poll.image_url}
            alt={poll.title}
            borderRadius="lg"
            maxH="200px"
            objectFit="cover"
          />
        )}

        <Heading size="md">{poll.title}</Heading>

        {poll.description && (
          <Text fontSize="sm" color="gray.600">
            {poll.description}
          </Text>
        )}

        {!isActive && (
          <Badge colorScheme="orange">Anketa je momentálně uzavřena</Badge>
        )}

        {isActive && (
          <>
            {resolvedStyle === 'rating-stars' && (
              <VStack align="stretch" spacing={2}>
                <HStack>
                  {Array.from({ length: maxRating }).map((_, i) => (
                    <StarIcon
                      key={i}
                      boxSize={6}
                      cursor="pointer"
                      color={i < (ratingValue || 0) ? 'yellow.400' : 'gray.300'}
                      onClick={() => selectOptionForRating(i + 1)}
                    />
                  ))}
                  <Text ml={2}>{ratingValue ? `${ratingValue}/${maxRating}` : 'Vyberte hodnocení'}</Text>
                </HStack>
              </VStack>
            )}
            {resolvedStyle === 'rating-scale' && (
              <HStack flexWrap="wrap" spacing={2}>
                {Array.from({ length: maxRating }).map((_, i) => (
                  <Button
                    key={i}
                    size="sm"
                    variant={ratingValue === i + 1 ? 'solid' : 'outline'}
                    colorScheme="blue"
                    onClick={() => selectOptionForRating(i + 1)}
                  >
                    {i + 1}
                  </Button>
                ))}
              </HStack>
            )}
            {resolvedStyle === 'choices-chips' && (
              <VStack align="stretch" spacing={2}>
                {poll.allow_multiple && (
                  <Text fontSize="sm" color="gray.500">Vyberte až {poll.max_choices} možností</Text>
                )}
                <HStack flexWrap="wrap" spacing={2}>
                  {poll.options.map((option) => {
                    const isSelected = selectedOptions.includes(option.id);
                    return (
                      <Button
                        key={option.id}
                        size="sm"
                        borderRadius="full"
                        variant={isSelected ? 'solid' : 'outline'}
                        colorScheme="blue"
                        onClick={() => handleOptionClick(option.id)}
                      >
                        {option.text}
                      </Button>
                    );
                  })}
                </HStack>
              </VStack>
            )}
            {resolvedStyle === 'choices-cards' && (
              <VStack spacing={3} align="stretch">
                {poll.options.map((option) => {
                  const isSelected = selectedOptions.includes(option.id);
                  return (
                    <Box
                      key={option.id}
                      p={3}
                      borderWidth={isSelected ? '2px' : '1px'}
                      borderColor={isSelected ? 'blue.400' : borderColor}
                      borderRadius="md"
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                      onClick={() => handleOptionClick(option.id)}
                    >
                      <HStack align="start" spacing={3}>
                        {option.image_url && (
                          <Image src={option.image_url} alt={option.text} boxSize="48px" objectFit="cover" borderRadius="md" />
                        )}
                        <VStack align="start" spacing={1} flex={1}>
                          <Text fontWeight="medium">{option.text}</Text>
                          {option.description && (
                            <Text fontSize="xs" color="gray.500">{option.description}</Text>
                          )}
                        </VStack>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
            {resolvedStyle === 'choices-list' && (
              <>
                {poll.allow_multiple ? (
                  <CheckboxGroup
                    value={selectedOptions.map(String)}
                    onChange={handleMultipleChoice}
                  >
                    <VStack spacing={2} align="stretch">
                      <Text fontSize="sm" color="gray.500">
                        Vyberte až {poll.max_choices} možností
                      </Text>
                      {poll.options.map((option) => (
                        <Box
                          key={option.id}
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          _hover={{ bg: hoverBg }}
                          cursor="pointer"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleOptionClick(option.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleOptionClick(option.id);
                            }
                          }}
                        >
                          <Checkbox value={String(option.id)} onClick={(e) => e.stopPropagation()}>
                            <VStack align="start" spacing={1}>
                              <Text>{option.text}</Text>
                              {option.description && (
                                <Text fontSize="xs" color="gray.500">
                                  {option.description}
                                </Text>
                              )}
                            </VStack>
                          </Checkbox>
                        </Box>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                ) : (
                  <RadioGroup
                    value={selectedOptions[0]?.toString() || ''}
                    onChange={handleSingleChoice}
                  >
                    <VStack spacing={2} align="stretch">
                      {poll.options.map((option) => (
                        <Box
                          key={option.id}
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          _hover={{ bg: hoverBg }}
                          cursor="pointer"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleOptionClick(option.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleOptionClick(option.id);
                            }
                          }}
                        >
                          <Radio value={String(option.id)} onClick={(e) => e.stopPropagation()}>
                            <VStack align="start" spacing={1}>
                              <Text>{option.text}</Text>
                              {option.description && (
                                <Text fontSize="xs" color="gray.500">
                                  {option.description}
                                </Text>
                              )}
                              {option.player && (
                                <HStack spacing={2}>
                                  {option.player.image_url && (
                                    <Image
                                      src={option.player.image_url}
                                      alt={`${option.player.first_name} ${option.player.last_name}`}
                                      boxSize="24px"
                                      borderRadius="full"
                                    />
                                  )}
                                  <Text fontSize="xs" color="gray.500">
                                    #{option.player.jersey_number} {option.player.first_name}{' '}
                                    {option.player.last_name}
                                  </Text>
                                </HStack>
                              )}
                            </VStack>
                          </Radio>
                        </Box>
                      ))}
                    </VStack>
                  </RadioGroup>
                )}
              </>
            )}

            {isAuthenticated ? (
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">Jméno (volitelné)</FormLabel>
                  <Input
                    size="sm"
                    autoComplete="name"
                    value={voterName || ((user as any)?.name || '')}
                    onChange={(e) => setVoterName(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">E-mail (volitelné)</FormLabel>
                  <Input
                    size="sm"
                    type="email"
                    autoComplete="email"
                    value={voterEmail || (user?.email || '')}
                    onChange={(e) => setVoterEmail(e.target.value)}
                  />
                </FormControl>
              </VStack>
            ) : (
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">Jméno (volitelné)</FormLabel>
                  <Input
                    size="sm"
                    autoComplete="name"
                    value={voterName}
                    onChange={(e) => setVoterName(e.target.value)}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">E-mail (volitelné)</FormLabel>
                  <Input
                    size="sm"
                    type="email"
                    autoComplete="email"
                    value={voterEmail}
                    onChange={(e) => setVoterEmail(e.target.value)}
                  />
                </FormControl>
                <Text fontSize="xs" color="gray.500">
                  Přihlášením se jméno a e-mail doplní automaticky.{' '}
                  <Link as={RouterLink} color="blue.500" to="/login" state={{ from: location }}>
                    {t('auth.login')}
                  </Link>
                </Text>
              </VStack>
            )}

            <Button
              colorScheme="blue"
              onClick={handleVote}
              isLoading={voteMutation.isPending}
              isDisabled={!isActive || selectedOptions.length === 0}
            >
              Hlasovat
            </Button>
          </>
        )}

        {canShowResults && !showingResults && (
          <Button variant="outline" onClick={loadResults} size="sm">
            Zobrazit výsledky
          </Button>
        )}

        <Text fontSize="xs" color="gray.500" textAlign="center">
          Celkem hlasů: {liveResultsData?.total_votes ?? poll.total_votes}
        </Text>
      </VStack>
    </Box>
  );
};

export default PollCard;
